import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, User, Menu, X, Search, LogOut, LayoutDashboard, GitCompareArrows, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCompare } from "@/context/CompareContext";
import { useWishlist } from "@/context/WishlistContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { ids } = useCompare();
  const { count: wishCount } = useWishlist();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/shop?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const links = [
    { to: "/shop", label: "Shop All" },
    { to: "/shop?recipient=For Her", label: "For Her" },
    { to: "/shop?occasion=Anniversary", label: "Occasions" },
    { to: "/festival/diwali", label: "Festivals" },
    { to: "/sell", label: "Sell on HGK" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-sand/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <span className="font-serif text-xl sm:text-2xl tracking-tight text-clay">handmade<span className="text-forest italic">giftkart</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link key={l.label} to={l.to} className="text-sm text-ink/80 hover:text-clay link-underline" data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex items-center bg-surface rounded-full px-3 py-1.5 border border-line">
            <Search className="w-4 h-4 text-mute" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search gifts…"
              className="bg-transparent outline-none text-sm px-2 w-28 lg:w-40"
              data-testid="search-input"
            />
          </form>

          <Link to="/compare" className="relative p-2 rounded-full hover:bg-surface transition-colors" data-testid="compare-link" aria-label="Compare">
            <GitCompareArrows className="w-5 h-5" />
            {ids.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-terra text-white text-[10px] w-4 h-4 rounded-full grid place-items-center">{ids.length}</span>}
          </Link>

          <Link to={user ? "/account?tab=saved" : "/login"} className="relative p-2 rounded-full hover:bg-surface transition-colors" data-testid="wishlist-link" aria-label="Saved">
            <Heart className="w-5 h-5" />
            {wishCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-terra text-white text-[10px] w-4 h-4 rounded-full grid place-items-center">{wishCount}</span>}
          </Link>

          <Link to="/cart" className="relative p-2 rounded-full hover:bg-surface transition-colors" data-testid="cart-link" aria-label="Cart">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-clay text-white text-[10px] w-4 h-4 rounded-full grid place-items-center">{count}</span>}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-1">
              <Link to={user.role === "admin" ? "/admin" : user.role === "seller" ? "/seller" : "/account"} className="p-2 rounded-full hover:bg-surface transition-colors" data-testid="account-link" aria-label="Account">
                {user.role === "seller" || user.role === "admin" ? <LayoutDashboard className="w-5 h-5" /> : <User className="w-5 h-5" />}
              </Link>
              <button onClick={logout} className="p-2 rounded-full hover:bg-surface transition-colors" data-testid="logout-btn" aria-label="Logout">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden sm:inline-flex items-center rounded-full bg-clay text-sand text-sm px-4 py-2 hover:bg-forest transition-colors" data-testid="signin-btn">
              Sign in
            </Link>
          )}

          <button onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-sand border-t border-line px-5 py-5 space-y-3" data-testid="mobile-menu">
          <form onSubmit={submitSearch} className="flex items-center bg-surface rounded-full px-3 py-2 border border-line mb-2">
            <Search className="w-4 h-4 text-mute" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search gifts…" className="bg-transparent outline-none text-sm px-2 flex-1" />
          </form>
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-ink/80 hover:text-clay">{l.label}</Link>
          ))}
          <div className="pt-2 border-t border-line flex items-center gap-3">
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : user.role === "seller" ? "/seller" : "/account"} onClick={() => setOpen(false)} className="flex-1 text-center rounded-full bg-surface py-2 text-sm">My Account</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="flex-1 rounded-full bg-clay text-sand py-2 text-sm">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center rounded-full border border-clay text-clay py-2 text-sm">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center rounded-full bg-clay text-sand py-2 text-sm">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
